import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Select, { components } from 'react-select';
import { CONFIG } from 'variables/config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    MdStorefront,
    MdVerifiedUser,
    MdAccountBalanceWallet,
    MdCheck,
    MdClose,
    MdBarChart,
    MdLocationOn,
    MdSchedule,
    MdShoppingCart,
    MdFilterAltOff,
    MdAdd,
} from 'react-icons/md';
import { useWalletBalance } from 'services/walletService';
import { useIndustries } from 'services/industryService';
import { frenchAreas } from 'variables/industries';
import PurchaseSuccessModal, { PurchasedLead } from './components/PurchaseSuccessModal';

// Deliberately minimal — the backend (marketplaceController.ts) never sends anything that
// could identify or contact the lead (email, phone, name, exact address, seller identity)
// before it's bought. Only the full lead (via /lead/list) exposes the rest, once owned.
type MarketplaceLead = {
    _id: string;
    score?: number | null;
    industry?: string;
    subIndustry?: string;
    price?: number;
    location?: string | null;
    rgpdCompliant?: boolean;
    createdAt?: string;
    // Cosmetic "was more expensive" badge — see randomDiscountPourcentage in
    // marketplaceController.ts. Not tied to any real price history.
    discountPourcentage?: number;
};

const PAGE_SIZE = 25;

// react-select ships unstyled by default — align it with the leadrs design tokens (rounded-xl,
// leadrs-stroke/leadrs-brand, Geist) and match the h-10 height of the other toolbar filters.
const regionSelectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: 40,
        height: 40,
        borderRadius: 12,
        borderColor: state.isFocused ? '#1C0F53' : '#F3EDE4',
        boxShadow: 'none',
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        flexWrap: 'nowrap',
        '&:hover': { borderColor: '#1C0F53' },
    }),
    valueContainer: (base: any) => ({
        ...base,
        height: 40,
        flexWrap: 'nowrap',
        overflow: 'hidden',
    }),
    input: (base: any) => ({ ...base, margin: 0 }),
    indicatorsContainer: (base: any) => ({ ...base, height: 40 }),
    placeholder: (base: any) => ({ ...base, color: '#676767' }),
    menu: (base: any) => ({
        ...base,
        borderRadius: 12,
        border: '1px solid #F3EDE4',
        boxShadow: '0 2px 8px rgba(20,20,18,0.08)',
        overflow: 'hidden',
        zIndex: 20,
    }),
    option: (base: any, state: any) => ({
        ...base,
        fontFamily: 'Geist, sans-serif',
        fontSize: 14,
        backgroundColor: state.isSelected ? '#1C0F53' : state.isFocused ? '#EAE6FF' : '#ffffff',
        color: state.isSelected ? '#ffffff' : '#141412',
        cursor: 'pointer',
    }),
    dropdownIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#1C0F53' } }),
    clearIndicator: (base: any) => ({ ...base, color: '#676767', '&:hover': { color: '#D63C3C' } }),
    indicatorSeparator: (base: any) => ({ ...base, backgroundColor: '#F3EDE4' }),
};

// Selected departments render as a single truncated line (ellipsis) instead of wrapping
// pills, so picking several doesn't grow the toolbar's row height.
const RegionValueContainer = ({ children, ...props }: any) => {
    const values = props.getValue();
    const input = Array.isArray(children) ? children[1] : children;
    if (!values.length) {
        return <components.ValueContainer {...props}>{children}</components.ValueContainer>;
    }
    return (
        <components.ValueContainer {...props}>
            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap font-geist text-xs text-leadrs-text">
                {values.map((v: any) => v.label).join(', ')}
            </span>
            {input}
        </components.ValueContainer>
    );
};

// Emoji + badge color per top-level industry category (see 001-populate-industries.ts for the
// canonical category list). Cycles through the leadrs soft/focus color pairs — an industry
// not in the map (a future category) falls back to a neutral badge rather than crashing.
const INDUSTRY_STYLES: Record<string, { emoji: string; badgeClass: string }> = {
    'Rénovation': { emoji: '🔧', badgeClass: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus' },
    'Formation': { emoji: '🎓', badgeClass: 'bg-leadrs-blue-soft text-leadrs-blue-focus' },
    'Assurance': { emoji: '🛡️', badgeClass: 'bg-leadrs-purple-soft text-leadrs-purple-focus' },
    'Investissement': { emoji: '📈', badgeClass: 'bg-leadrs-green-soft text-leadrs-green-focus' },
    'Immobilier': { emoji: '🏠', badgeClass: 'bg-leadrs-brown-soft text-leadrs-brown-focus' },
    'Banque': { emoji: '💳', badgeClass: 'bg-leadrs-blue-soft text-leadrs-blue-focus' },
    'Santé': { emoji: '🏥', badgeClass: 'bg-leadrs-purple-soft text-leadrs-purple-focus' },
    'Voyages': { emoji: '✈️', badgeClass: 'bg-leadrs-blue-soft text-leadrs-blue-focus' },
    'Services Juridique': { emoji: '⚖️', badgeClass: 'bg-leadrs-red-soft text-leadrs-red-focus' },
    'Services de transport et mobilité': { emoji: '🚗', badgeClass: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus' },
    'Services de déménagement et logistique': { emoji: '📦', badgeClass: 'bg-leadrs-brown-soft text-leadrs-brown-focus' },
    'Voyance et ésotérisme': { emoji: '🔮', badgeClass: 'bg-leadrs-purple-soft text-leadrs-purple-focus' },
    'Leads liés aux seniors': { emoji: '👴', badgeClass: 'bg-leadrs-yellow-soft text-leadrs-yellow-focus' },
    'Leads urgence habitat': { emoji: '🚨', badgeClass: 'bg-leadrs-red-soft text-leadrs-red-focus' },
};
const DEFAULT_INDUSTRY_STYLE = { emoji: '📋', badgeClass: 'bg-leadrs-stroke text-leadrs-text-soft' };
const getIndustryStyle = (industry?: string) => (industry && INDUSTRY_STYLES[industry]) || DEFAULT_INDUSTRY_STYLE;

const scoreColor = (score?: number) => {
    if (score == null) return 'text-leadrs-text-soft';
    if (score >= 7) return 'text-leadrs-success';
    if (score >= 4) return 'text-leadrs-info';
    return 'text-leadrs-wrong';
};

// Circular score gauge — the ring fill is the score's share of /10, colored by the same
// green/orange/red tiers as scoreColor. `stroke="currentColor"` lets the ring inherit its
// color straight from the wrapping span's text color class.
const ScoreCircle = ({ score }: { score?: number | null }) => {
    const size = 40;
    const strokeWidth = 3;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const pct = score == null ? 0 : Math.max(0, Math.min(10, score)) / 10;
    const dashOffset = circumference * (1 - pct);

    return (
        <span className={`relative inline-flex h-10 w-10 flex-shrink-0 items-center justify-center ${scoreColor(score ?? undefined)}`}>
            <svg width={size} height={size} className="-rotate-90">
                <circle cx={size / 2} cy={size / 2} r={radius} fill="none" strokeWidth={strokeWidth} className="stroke-leadrs-stroke" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    strokeWidth={strokeWidth}
                    strokeLinecap="round"
                    stroke="currentColor"
                    strokeDasharray={circumference}
                    strokeDashoffset={dashOffset}
                />
            </svg>
            <span className="absolute font-geist text-base font-bold">{score ?? '—'}</span>
        </span>
    );
};

// Original price a discount % implies, given `price` is already the final amount charged.
const originalPriceFromDiscount = (price: number, discountPourcentage?: number) => {
    if (!discountPourcentage) return null;
    return price / (1 - discountPourcentage / 100);
};

// Deterministic PRNG seeded by lead id, so a row's sparkline doesn't reshuffle on every
// re-render. Purely decorative — there's no real price-history data behind it.
const seededRandom = (seed: string) => {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (Math.imul(h, 31) + seed.charCodeAt(i)) | 0;
    return () => {
        h = (Math.imul(h, 1103515245) + 12345) | 0;
        return ((h >>> 0) % 10000) / 10000;
    };
};

const buildSparklinePoints = (seed: string, pointCount = 8) => {
    const rand = seededRandom(seed);
    let value = 65 + rand() * 25;
    const points = [value];
    for (let i = 1; i < pointCount; i++) {
        value = Math.max(8, value - rand() * 14);
        points.push(value);
    }
    return points;
};

// Thin single-series micro line + recessive area fill, anchored to the cell's baseline —
// no axes/labels/tooltip, this is a decorative trend indicator, not a chart to read values off.
const Sparkline = ({ seed }: { seed: string }) => {
    const points = useMemo(() => buildSparklinePoints(seed), [seed]);
    const width = 88;
    const height = 32;
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max - min || 1;
    const stepX = width / (points.length - 1);
    const coords = points.map((v, i) => [i * stepX, height - ((v - min) / range) * height]);
    const linePath = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
    const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            <path d={areaPath} fill="#F3EDE4" />
            <path d={linePath} fill="none" stroke="#141412" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

const PriceCell = ({ lead }: { lead: MarketplaceLead }) => {
    const originalPrice = originalPriceFromDiscount(lead.price ?? 0, lead.discountPourcentage);
    return (
        <>
            <div className="flex w-fit items-center gap-2">
                <span className="font-geist text-base font-bold text-leadrs-text">
                    {(lead.price ?? 0).toFixed(2)} €
                </span>
                {!!lead.discountPourcentage && (
                    <span className="rounded-full bg-leadrs-red-soft px-1.5 py-0.5 font-geist text-[11px] font-semibold text-leadrs-red-focus">
                        -{lead.discountPourcentage}%
                    </span>
                )}
            </div>
            {!!lead.discountPourcentage && (
                <span className="font-geist text-xs text-leadrs-text-soft line-through">
                    {originalPrice?.toFixed(2)} €
                </span>
            )}
        </>
    );
};

const timeAgo = (date: string) => {
    const diffInSeconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    const days    = Math.floor(diffInSeconds / (3600 * 24));
    const hours   = Math.floor((diffInSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((diffInSeconds % 3600) / 60);
    const seconds = diffInSeconds % 60;
    if (days    > 0) return `il y a ${days} jour${days > 1 ? 's' : ''}`;
    if (hours   > 0) return `il y a ${hours}h`;
    if (minutes > 0) return `il y a ${minutes} min`;
    return `il y a ${seconds}s`;
};

const isWithin30Minutes = (date: string) => {
    return (Date.now() - new Date(date).getTime()) / (1000 * 60) <= 30;
};

type SortOption = 'price_asc' | 'price_desc' | 'newest';

type MarketplaceFilters = {
    search: string;
    industryFilter: string;
    subIndustryFilter: string;
    regions: string[];
    rgpdOnly: boolean;
    recentOnly: boolean;
    sort: SortOption;
};

// Filters persist across visits so returning to the Bourse du Lead re-applies (and
// re-fetches with) whatever the buyer last set, instead of resetting to a blank slate.
const FILTERS_STORAGE_KEY = 'bourseDuLead_filters';
const DEFAULT_FILTERS: MarketplaceFilters = {
    search: '',
    industryFilter: '',
    subIndustryFilter: '',
    regions: [],
    rgpdOnly: false,
    recentOnly: false,
    sort: 'newest',
};

const loadStoredFilters = (): MarketplaceFilters => {
    try {
        const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
        if (!raw) return DEFAULT_FILTERS;
        return { ...DEFAULT_FILTERS, ...JSON.parse(raw) };
    } catch {
        return DEFAULT_FILTERS;
    }
};

const SORT_OPTIONS: { value: SortOption; label: string }[] = [
    { value: 'newest', label: 'Plus récents' },
    { value: 'price_asc', label: 'Moins chers' },
    { value: 'price_desc', label: 'Plus chers' },
];

const LeadMarketplace = () => {
    const nav = useNavigate();
    const { walletBalance, loading: walletLoading, refetch: refetchWallet } = useWalletBalance();
    const { industries } = useIndustries();

    const [leads, setLeads] = useState<MarketplaceLead[]>([]);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [buyingLeadId, setBuyingLeadId] = useState<string | null>(null);
    // items[index] is the lead currently shown; a batch purchase fills items with every
    // lead bought in that run so the same modal can be browsed with Précédent/Suivant
    // instead of popping one modal per lead.
    const [purchaseModal, setPurchaseModal] = useState<{ items: { lead: PurchasedLead; pricePaid: number | null }[]; index: number } | null>(null);
    // Inline "click again to confirm" state, right in the row's Actions cell — no top alert,
    // no window.confirm. Auto-reverts to the plain "Payer" button if left untouched.
    const [confirmingLeadId, setConfirmingLeadId] = useState<string | null>(null);
    const confirmTimers = useRef<Record<string, any>>({});

    // Multi-select cart for "Tout acheter" — keyed by lead id (not by page) so a selection
    // survives pagination/filtering. Purchases are sent one request at a time (see
    // handleBuyAll below), reusing the same single-lead /buy endpoint as the per-row button.
    const [selectedLeads, setSelectedLeads] = useState<Record<string, MarketplaceLead>>({});
    const [batchBuying, setBatchBuying] = useState(false);
    const [batchProgress, setBatchProgress] = useState<{ done: number; total: number } | null>(null);

    const [initialFilters] = useState(loadStoredFilters);
    const [search, setSearch] = useState(initialFilters.search);
    const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search);
    const [industryFilter, setIndustryFilter] = useState(initialFilters.industryFilter);
    const [subIndustryFilter, setSubIndustryFilter] = useState(initialFilters.subIndustryFilter);
    const [regions, setRegions] = useState<string[]>(initialFilters.regions);
    const [rgpdOnly, setRgpdOnly] = useState(initialFilters.rgpdOnly);
    const [recentOnly, setRecentOnly] = useState(initialFilters.recentOnly);
    // Sort always starts on "Plus récents" — unlike the other filters below, it's
    // deliberately not seeded from localStorage nor persisted back into it.
    const [sort, setSort] = useState<SortOption>(DEFAULT_FILTERS.sort);
    // Baseline count fetched once, ignoring all filters — "X sur Y" needs Y to stay fixed
    // while X (the filtered `total`) moves as the buyer narrows things down.
    const [totalUnfiltered, setTotalUnfiltered] = useState<number | null>(null);
    // Filter changes fire overlapping requests (the endpoint is slow, prices are resolved
    // live): only the latest one may write its leads/total, or a stale response that lands
    // last shows a count that doesn't match the filters on screen.
    const latestFetchId = useRef(0);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        try {
            localStorage.setItem(
                FILTERS_STORAGE_KEY,
                JSON.stringify({ search, industryFilter, subIndustryFilter, regions, rgpdOnly, recentOnly })
            );
        } catch {
            // Best-effort only — a private-browsing quota error shouldn't break filtering.
        }
    }, [search, industryFilter, subIndustryFilter, regions, rgpdOnly, recentOnly]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;
        axios
            .get(`${CONFIG.apiUrl}/marketplace/leads`, { headers: { Authorization: `Bearer ${token}` }, params: { page: 1, limit: 1 } })
            .then((res) => setTotalUnfiltered(res.data?.total ?? null))
            .catch(() => {});
    }, []);

    const fetchLeads = async (targetPage: number) => {
        const token = localStorage.getItem('token');
        if (!token) return;
        const fetchId = ++latestFetchId.current;
        setLoading(true);
        try {
            const params: any = { page: targetPage, limit: PAGE_SIZE, sort };
            if (industryFilter) params.industry = industryFilter;
            if (subIndustryFilter) params.subIndustry = subIndustryFilter;
            if (regions.length) params.region = regions;
            if (rgpdOnly) params.rgpd = 'true';
            if (recentOnly) params.recentOnly = 'true';
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            const res = await axios.get(`${CONFIG.apiUrl}/marketplace/leads`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            if (fetchId !== latestFetchId.current) return;
            setLeads(res.data?.leads || []);
            setTotal(res.data?.total ?? 0);
            setTotalPages(res.data?.totalPages ?? 1);
            setPage(res.data?.page ?? targetPage);
        } catch (err) {
            if (fetchId !== latestFetchId.current) return;
            toast.error('Échec du chargement de la Bourse du Lead');
        } finally {
            if (fetchId === latestFetchId.current) setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [industryFilter, subIndustryFilter, regions, rgpdOnly, recentOnly, debouncedSearch, sort]);

    useEffect(() => {
        const timers = confirmTimers.current;
        return () => { Object.values(timers).forEach((t) => clearTimeout(t)); };
    }, []);

    const subIndustryOptions = useMemo(() => {
        return industries.find((i) => i.name === industryFilter)?.subIndustries || [];
    }, [industries, industryFilter]);

    const activeFilterCount = [
        !!industryFilter,
        !!subIndustryFilter,
        regions.length > 0,
        rgpdOnly,
        recentOnly,
        !!debouncedSearch.trim(),
        sort !== DEFAULT_FILTERS.sort,
    ].filter(Boolean).length;

    const selectedList = useMemo(() => Object.values(selectedLeads), [selectedLeads]);
    const selectedCount = selectedList.length;
    const selectedTotal = useMemo(() => selectedList.reduce((sum, l) => sum + (l.price ?? 0), 0), [selectedList]);

    const toggleSelectLead = (lead: MarketplaceLead) => {
        setSelectedLeads((prev) => {
            if (prev[lead._id]) {
                const next = { ...prev };
                delete next[lead._id];
                return next;
            }
            return { ...prev, [lead._id]: lead };
        });
    };

    const handleClearSelection = () => setSelectedLeads({});

    const handleResetFilters = () => {
        setSearch(DEFAULT_FILTERS.search);
        setDebouncedSearch(DEFAULT_FILTERS.search);
        setIndustryFilter(DEFAULT_FILTERS.industryFilter);
        setSubIndustryFilter(DEFAULT_FILTERS.subIndustryFilter);
        setRegions(DEFAULT_FILTERS.regions);
        setRgpdOnly(DEFAULT_FILTERS.rgpdOnly);
        setRecentOnly(DEFAULT_FILTERS.recentOnly);
        setSort(DEFAULT_FILTERS.sort);
        try {
            localStorage.removeItem(FILTERS_STORAGE_KEY);
        } catch {
            // Best-effort only.
        }
    };

    const clearConfirmTimer = (leadId: string) => {
        if (confirmTimers.current[leadId]) {
            clearTimeout(confirmTimers.current[leadId]);
            delete confirmTimers.current[leadId];
        }
    };

    // First click arms the row's inline confirmation (Annuler / Confirmer), right where the
    // buyer clicked — a second click there actually buys. Auto-cancels after a few seconds
    // if left untouched.
    const handleBuyClick = (lead: MarketplaceLead) => {
        if (confirmingLeadId === lead._id) {
            clearConfirmTimer(lead._id);
            setConfirmingLeadId(null);
            executeBuy(lead);
            return;
        }
        if (confirmingLeadId) clearConfirmTimer(confirmingLeadId);
        setConfirmingLeadId(lead._id);
        confirmTimers.current[lead._id] = setTimeout(() => {
            setConfirmingLeadId((current) => (current === lead._id ? null : current));
        }, 4000);
    };

    const cancelConfirm = (leadId: string) => {
        clearConfirmTimer(leadId);
        setConfirmingLeadId(null);
    };

    const executeBuy = async (lead: MarketplaceLead) => {
        setBuyingLeadId(lead._id);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(
                `${CONFIG.apiUrl}/marketplace/leads/${lead._id}/buy`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setLeads((prev) => prev.filter((l) => l._id !== lead._id));
            setTotal((prev) => Math.max(0, prev - 1));
            setSelectedLeads((prev) => {
                if (!prev[lead._id]) return prev;
                const next = { ...prev };
                delete next[lead._id];
                return next;
            });
            refetchWallet();
            setPurchaseModal({ items: [{ lead: res.data?.lead, pricePaid: res.data?.price ?? lead.price ?? null }], index: 0 });
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Échec de l'achat");
            fetchLeads(page);
        } finally {
            setBuyingLeadId(null);
        }
    };

    // Buys every selected lead through the existing single-lead endpoint, one request at a
    // time — each `await` only moves to the next lead once the previous purchase request has
    // resolved (success or failure), so there's never more than one in-flight purchase and no
    // risk of racing the wallet balance. The cart bar shows a spinner + "done/total" while this
    // runs, and the wallet balance (refetched after every successful purchase) updates live.
    const handleBuyAll = async () => {
        const ids = Object.keys(selectedLeads);
        if (!ids.length || batchBuying) return;

        setBatchBuying(true);
        setBatchProgress({ done: 0, total: ids.length });

        const token = localStorage.getItem('token');
        const purchased: { lead: PurchasedLead; pricePaid: number | null }[] = [];
        let failureCount = 0;

        for (let i = 0; i < ids.length; i++) {
            const id = ids[i];
            const originalLead = selectedLeads[id];
            try {
                const res = await axios.post(`${CONFIG.apiUrl}/marketplace/leads/${id}/buy`, {}, { headers: { Authorization: `Bearer ${token}` } });
                purchased.push({ lead: res.data?.lead, pricePaid: res.data?.price ?? originalLead?.price ?? null });
                setLeads((prev) => prev.filter((l) => l._id !== id));
                setTotal((prev) => Math.max(0, prev - 1));
                refetchWallet();
            } catch (error) {
                failureCount += 1;
            } finally {
                setSelectedLeads((prev) => {
                    if (!prev[id]) return prev;
                    const next = { ...prev };
                    delete next[id];
                    return next;
                });
                setBatchProgress({ done: i + 1, total: ids.length });
            }
        }

        setBatchBuying(false);
        setBatchProgress(null);
        // Same success modal as a single purchase, browsable via Précédent/Suivant across
        // every lead bought in this batch — replaces the old "X leads achetés" toast.
        if (purchased.length > 0) {
            setPurchaseModal({ items: purchased, index: 0 });
        }
        if (failureCount > 0) {
            toast.error(`${failureCount} achat${failureCount > 1 ? 's' : ''} ont échoué`);
        }
    };

    const handleViewMyLeads = () => {
        localStorage.setItem('leads_selectedOffer', 'BOURSE_DU_LEAD');
        nav('/admin/leads');
    };

    const handleContinueCategory = () => {
        const currentLead = purchaseModal?.items[purchaseModal.index]?.lead;
        if (currentLead) {
            setIndustryFilter(currentLead.industry || '');
            setSubIndustryFilter(currentLead.subIndustry || '');
        }
        setPurchaseModal(null);
    };

    const handlePrevPurchase = () => {
        setPurchaseModal((prev) => (prev ? { ...prev, index: Math.max(0, prev.index - 1) } : prev));
    };

    const handleNextPurchase = () => {
        setPurchaseModal((prev) => (prev ? { ...prev, index: Math.min(prev.items.length - 1, prev.index + 1) } : prev));
    };

    return (
        <div className="h-full w-full">
            <style>{`
                @keyframes confirmPop {
                    0% { opacity: 0; transform: scale(0.85); }
                    60% { opacity: 1; transform: scale(1.05); }
                    100% { opacity: 1; transform: scale(1); }
                }
                .confirm-pop { animation: confirmPop 0.22s ease-out; transform-origin: right center; }
            `}</style>
            <ToastContainer position="bottom-right" />
            <PurchaseSuccessModal
                isOpen={!!purchaseModal}
                onClose={() => setPurchaseModal(null)}
                lead={purchaseModal?.items[purchaseModal.index]?.lead ?? null}
                pricePaid={purchaseModal?.items[purchaseModal.index]?.pricePaid ?? null}
                onViewMyLeads={handleViewMyLeads}
                onContinueCategory={handleContinueCategory}
                currentIndex={purchaseModal?.index}
                totalCount={purchaseModal?.items.length}
                onPrev={handlePrevPurchase}
                onNext={handleNextPurchase}
            />

            <div className="space-y-5">

                <div className="rounded-xl border border-leadrs-stroke bg-white p-5">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <h2 className="font-crimson text-2xl font-semibold text-leadrs-text">Trouvez vos leads</h2>
                        <span className="font-geist text-sm text-leadrs-text-soft">
                            {total} lead{total > 1 ? 's' : ''} sur {totalUnfiltered ?? total} correspond{total > 1 ? 'ent' : ''} à vos critères
                        </span>
                    </div>

                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <label className="mb-1.5 block font-geist text-xs text-leadrs-text-soft">Industrie</label>
                            <select
                                value={industryFilter}
                                onChange={(e) => { setIndustryFilter(e.target.value); setSubIndustryFilter(''); }}
                                className="h-10 w-full rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                            >
                                <option value="">Toutes les industries</option>
                                {industries.map((ind) => (
                                    <option key={ind._id} value={ind.name}>{ind.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="mb-1.5 block font-geist text-xs text-leadrs-text-soft">Produit</label>
                            <select
                                value={subIndustryFilter}
                                onChange={(e) => setSubIndustryFilter(e.target.value)}
                                disabled={!industryFilter}
                                className="h-10 w-full rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none disabled:cursor-not-allowed disabled:bg-leadrs-bg disabled:text-leadrs-text-soft"
                            >
                                <option value="">Tous les produits</option>
                                {subIndustryOptions.map((sub) => (
                                    <option key={sub.name} value={sub.name}>{sub.name}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="mb-1.5 block font-geist text-xs text-leadrs-text-soft">Département</label>
                            <Select
                                value={regions.map((r) => ({ value: r, label: r }))}
                                isMulti
                                onChange={(items) => setRegions(items.map((item) => item.value))}
                                name="regions"
                                options={frenchAreas.map((item) => ({ value: item, label: item }))}
                                styles={regionSelectStyles}
                                components={{ ValueContainer: RegionValueContainer }}
                                className="w-full"
                                classNamePrefix="select"
                                placeholder="Tous les départements"
                            />
                        </div>

                        <div>
                            <label className="mb-1.5 block font-geist text-xs text-leadrs-text-soft">Recherche</label>
                            <input
                                type="search"
                                placeholder="Rechercher…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="h-10 w-full rounded-xl border border-leadrs-stroke bg-white px-3 font-geist text-sm text-leadrs-text focus:border-leadrs-brand focus:outline-none"
                            />
                        </div>
                    </div>

                    <div className="my-4 border-t border-leadrs-stroke" />

                    <div className="flex flex-wrap items-center gap-3">
                        <span className="font-geist text-sm text-leadrs-text-soft">Trier par</span>
                        <div className="flex items-center gap-1 rounded-full bg-leadrs-bg p-1">
                            {SORT_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    onClick={() => setSort(opt.value)}
                                    className={`rounded-full px-3 py-1.5 font-geist text-sm font-medium transition ${
                                        sort === opt.value
                                            ? 'bg-white text-leadrs-text shadow-sm'
                                            : 'text-leadrs-text-soft hover:text-leadrs-text'
                                    }`}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>

                        <label className="flex h-9 items-center gap-2 rounded-full border border-leadrs-stroke bg-white px-3 font-geist text-xs text-leadrs-text-soft">
                            <input type="checkbox" checked={rgpdOnly} onChange={(e) => setRgpdOnly(e.target.checked)} />
                            RGPD conforme
                        </label>

                        <label className="flex h-9 items-center gap-2 rounded-full border border-leadrs-stroke bg-white px-3 font-geist text-xs text-leadrs-text-soft">
                            <input type="checkbox" checked={recentOnly} onChange={(e) => setRecentOnly(e.target.checked)} />
                            🔴 Nouveaux (&lt;30 min)
                        </label>

                        <button
                            onClick={handleResetFilters}
                            disabled={activeFilterCount === 0}
                            className="ml-auto flex h-9 items-center gap-1.5 rounded-full border border-leadrs-stroke bg-white px-4 font-geist text-sm font-medium text-leadrs-text-soft transition hover:border-leadrs-brand hover:text-leadrs-text disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdFilterAltOff size={15} />
                            Réinitialiser
                            {activeFilterCount > 0 && (
                                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-leadrs-brand font-geist text-[10px] font-bold text-white">
                                    {activeFilterCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-xl border border-leadrs-stroke bg-white">
                    <table className="w-full min-w-[900px] table-auto">
                        <thead>
                            <tr className="border-b border-leadrs-stroke text-left font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                <th className="px-4 py-3">Thématique</th>
                                <th className="px-4 py-3">Localisation</th>
                                <th className="px-4 py-3">Score</th>
                                <th className="px-4 py-3">Prix</th>
                                <th className="px-4 py-3">Tendance</th>
                                <th className="px-4 py-3">RGPD</th>
                                <th className="px-4 py-3">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center font-geist text-sm text-leadrs-text-soft">Chargement…</td></tr>
                            ) : leads.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-8 text-center font-geist text-sm text-leadrs-text-soft">Aucun lead disponible pour ces filtres.</td></tr>
                            ) : (
                                leads.map((lead) => (
                                    <tr key={lead._id} className={`border-b border-leadrs-stroke last:border-0 hover:bg-leadrs-bg ${selectedLeads[lead._id] ? 'bg-leadrs-purple-soft/30' : ''}`}>
                                        <td className="px-4 py-3">
                                            <p className="font-geist text-sm font-semibold text-leadrs-text">{lead.subIndustry || lead.industry || '—'}</p>
                                            {lead.industry && (
                                                <span className={`mt-1 inline-flex w-fit items-center gap-1 rounded-full px-2 py-0.5 font-geist text-[11px] font-medium ${getIndustryStyle(lead.industry).badgeClass}`}>
                                                    <span>{getIndustryStyle(lead.industry).emoji}</span> {lead.industry}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <p className="flex items-center gap-1 font-geist text-sm text-leadrs-text">
                                                <MdLocationOn size={14} className="text-leadrs-text-soft" /> {lead.location || '—'}
                                            </p>
                                            {lead.createdAt && (
                                                <div className="mt-1 flex items-center gap-1.5">
                                                    {isWithin30Minutes(lead.createdAt) && (
                                                        <span className="relative flex h-2 w-2 flex-shrink-0">
                                                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-leadrs-red-focus opacity-75" />
                                                            <span className="relative inline-flex h-2 w-2 rounded-full bg-leadrs-red-focus" />
                                                        </span>
                                                    )}
                                                    <span className="flex items-center gap-1 font-geist text-xs text-leadrs-text-soft">
                                                        <MdSchedule size={12} /> {timeAgo(lead.createdAt)}
                                                    </span>
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <ScoreCircle score={lead.score} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <PriceCell lead={lead} />
                                        </td>
                                        <td className="px-4 py-3">
                                            <Sparkline seed={lead._id} />
                                        </td>
                                        <td className="px-4 py-3">
                                            {lead.rgpdCompliant ? (
                                                <span className="inline-flex items-center gap-1 rounded-full bg-leadrs-green-soft px-2 py-1 font-geist text-xs font-semibold text-leadrs-green-focus">
                                                    <MdVerifiedUser size={13} /> Conforme
                                                </span>
                                            ) : (
                                                <span className="font-geist text-xs text-leadrs-text-soft">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1.5">
                                                <button
                                                    type="button"
                                                    onClick={() => toggleSelectLead(lead)}
                                                    disabled={batchBuying || buyingLeadId === lead._id}
                                                    aria-label={selectedLeads[lead._id] ? 'Retirer du panier' : 'Ajouter au panier'}
                                                    aria-pressed={!!selectedLeads[lead._id]}
                                                    className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border-2 transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                                        selectedLeads[lead._id]
                                                            ? 'border-leadrs-brand bg-leadrs-purple-soft text-leadrs-brand'
                                                            : 'border-leadrs-text/20 bg-white text-leadrs-text hover:border-leadrs-brand hover:text-leadrs-brand'
                                                    }`}
                                                >
                                                    {selectedLeads[lead._id] ? <MdCheck size={18} /> : <MdAdd size={18} />}
                                                </button>
                                                {confirmingLeadId === lead._id ? (
                                                    <div className="confirm-pop flex items-center gap-1.5">
                                                        <button
                                                            onClick={() => cancelConfirm(lead._id)}
                                                            aria-label="Annuler"
                                                            className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg border border-leadrs-stroke bg-white text-leadrs-text-soft transition hover:bg-leadrs-bg"
                                                        >
                                                            <MdClose size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleBuyClick(lead)}
                                                            disabled={buyingLeadId === lead._id}
                                                            className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-leadrs-green-focus px-3 py-1.5 font-geist text-xs font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            <MdCheck size={14} />
                                                            {buyingLeadId === lead._id ? '...' : `Confirmer ${(lead.price ?? 0).toFixed(2)} €`}
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleBuyClick(lead)}
                                                        className="flex items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#6C5CE7] px-4 py-2 font-geist text-xs font-bold text-white transition hover:bg-[#5b4bd1]"
                                                    >
                                                        <MdShoppingCart size={15} />
                                                        Acheter ce lead
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2">
                        <button
                            onClick={() => fetchLeads(Math.max(1, page - 1))}
                            disabled={page <= 1}
                            className="rounded-lg border border-leadrs-stroke bg-white px-3 py-1.5 font-geist text-xs disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Précédent
                        </button>
                        <span className="font-geist text-xs text-leadrs-text-soft">Page {page} / {totalPages}</span>
                        <button
                            onClick={() => fetchLeads(Math.min(totalPages, page + 1))}
                            disabled={page >= totalPages}
                            className="rounded-lg border border-leadrs-stroke bg-white px-3 py-1.5 font-geist text-xs disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Suivant
                        </button>
                    </div>
                )}

                {selectedCount > 0 && (
                    <div className="pointer-events-none fixed inset-x-0 bottom-6 z-40 flex justify-center sm:left-[313px]">
                        <div className="pointer-events-auto flex w-fit max-w-[calc(100vw-2rem)] flex-wrap items-center gap-3 rounded-2xl border border-leadrs-stroke bg-white px-5 py-3 shadow-lg">
                            {batchBuying ? (
                                <>
                                    <span className="flex items-center gap-2 font-geist text-sm font-medium text-leadrs-text">
                                        <span className="h-4 w-4 flex-shrink-0 animate-spin rounded-full border-2 border-leadrs-brand border-t-transparent" />
                                        Achat en cours… {batchProgress ? `${batchProgress.done}/${batchProgress.total}` : ''}
                                    </span>
                                    <span className="font-geist text-xs text-leadrs-text-soft">
                                        Solde{walletLoading ? '…' : ` : ${walletBalance.toFixed(2)} €`}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="whitespace-nowrap font-geist text-sm text-leadrs-text">
                                        <strong>{selectedCount}</strong> lead{selectedCount > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''} · {selectedTotal.toFixed(2)} €
                                    </span>
                                    <button
                                        onClick={handleClearSelection}
                                        className="whitespace-nowrap rounded-xl border border-leadrs-stroke bg-white px-3 py-2 font-geist text-sm font-medium text-leadrs-text-soft transition hover:border-leadrs-brand hover:text-leadrs-text"
                                    >
                                        Vider
                                    </button>
                                    <button
                                        onClick={handleBuyAll}
                                        className="flex items-center gap-1.5 whitespace-nowrap rounded-xl bg-leadrs-brand px-4 py-2 font-geist text-sm font-bold text-white transition hover:opacity-90"
                                    >
                                        <MdShoppingCart size={16} />
                                        Tout acheter
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadMarketplace;
